import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, HelpCircle, ChevronDown, ChevronUp, Globe, DollarSign, 
  Users, Shield, Calendar, Music, Flame, Phone, Mail, AlertTriangle,
  CheckCircle, Info, Star, UserCheck, Settings, FileDown
} from "lucide-react";
import Footer from "../components/Footer";
import { ORG_PHONE, ORG_PHONE_RAW, ORG_ADDRESS, ORG_EMAIL_BOOKINGS, ORG_FULL_NAME } from "../constants";
import LanguageSelector from "../components/LanguageSelector";

const HelpPage = () => {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);
  const [activeTab, setActiveTab] = useState('karaoke');
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is logged in as admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        setIsAdmin(user?.is_admin || user?.is_master_admin || false);
      } catch {
        setIsAdmin(false);
      }
    }
  }, []);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // FAQ Data for Residents & KJs
  const residentFaqs = [
    {
      id: 'pricing',
      question: 'What are the Karaoke Booking prices?',
      answer: (
        <div className="space-y-3">
          <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded">
            <h4 className="font-bold text-blue-800 mb-2">📅 Pricing Schedule</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white p-3 border-2 border-black rounded">
                <p className="font-bold notranslate" translate="no">$90</p>
                <p className="text-sm text-gray-600">Weekdays (Mon - Fri)</p>
              </div>
              <div className="bg-white p-3 border-2 border-black rounded">
                <p className="font-bold notranslate" translate="no">$100</p>
                <p className="text-sm text-gray-600">Weekends &amp; Public Holidays</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Prices are configurable by Admin and may change. Check the booking calendar for the latest pricing.</p>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Time Slots:</strong> 1:00 PM - 5:00 PM (Afternoon) or 6:00 PM - 10:00 PM (Evening)
          </p>
        </div>
      )
    },
    {
      id: 'translator',
      question: 'How do I use the Language Translator?',
      answer: (
        <div className="space-y-3">
          <div className="bg-green-50 border-2 border-green-200 p-4 rounded">
            <h4 className="font-bold text-green-800 mb-2">🌐 Using the Globe Translator</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Click the <strong>Globe icon</strong> (🌐) in the top-right header</li>
              <li>Select your preferred language from the dropdown:
                <ul className="ml-6 mt-1 space-y-1">
                  <li>🇬🇧 English</li>
                  <li>🇨🇳 中文 (Chinese Simplified)</li>
                  <li>🇲🇾 Bahasa Melayu (Malay)</li>
                  <li>🇮🇳 தமிழ் (Tamil)</li>
                </ul>
              </li>
              <li>The page will translate automatically</li>
              <li>To return to English, click the Globe and select &quot;English&quot;</li>
            </ol>
          </div>
          <div className="bg-amber-50 border-2 border-amber-300 p-3 rounded">
            <p className="text-sm font-bold text-amber-800">⚠️ Protected Terms (Will NOT be translated):</p>
            <p className="text-sm mt-1">
              <span className="notranslate bg-gray-200 px-2 py-0.5 rounded mx-1" translate="no">YCK Court RN</span>
              <span className="notranslate bg-gray-200 px-2 py-0.5 rounded mx-1" translate="no">KJ</span>
              <span className="notranslate bg-gray-200 px-2 py-0.5 rounded mx-1" translate="no">Karaoke</span>
              <span className="notranslate bg-gray-200 px-2 py-0.5 rounded mx-1" translate="no">$90</span>
              <span className="notranslate bg-gray-200 px-2 py-0.5 rounded mx-1" translate="no">$100</span>
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'kj-role',
      question: 'What is a KJ and what are the two KJ roles?',
      answer: (
        <div className="space-y-3">
          <p><span className="notranslate font-bold" translate="no">KJ</span> stands for <span className="notranslate" translate="no">Karaoke</span> Jockey - a person who hosts and manages <span className="notranslate" translate="no">karaoke</span> sessions.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-green-50 border-2 border-green-500 p-4 rounded">
              <p className="font-bold text-green-700 flex items-center gap-2">
                🎤 <span className="notranslate" translate="no">External KJ</span>
              </p>
              <p className="text-sm mt-2">Receives <span className="notranslate font-bold" translate="no">$35</span> allowance per completed session</p>
            </div>
            <div className="bg-yellow-50 border-2 border-yellow-500 p-4 rounded">
              <p className="font-bold text-yellow-700 flex items-center gap-2">
                🏠 <span className="notranslate" translate="no">RN Member</span>
              </p>
              <p className="text-sm mt-2">Receives <span className="notranslate font-bold" translate="no">$0</span> allowance (volunteer basis)</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 italic">
            Note: Your <span className="notranslate" translate="no">KJ</span> role is assigned during registration and can only be changed by an Admin.
          </p>
        </div>
      )
    },
    {
      id: 'booking-process',
      question: 'How do I make a Karaoke Booking?',
      answer: (
        <div className="space-y-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>Go to the <strong><span className="notranslate" translate="no">Karaoke</span> Room</strong> booking page</li>
            <li>Select your preferred date on the calendar</li>
            <li>Choose an available time slot (Afternoon or Evening)</li>
            <li>Fill in your contact details</li>
            <li>Complete payment via onePA</li>
            <li>Receive confirmation email with your booking code</li>
          </ol>
          <div className="bg-red-50 border-2 border-red-300 p-3 rounded">
            <p className="text-sm font-bold text-red-700">⚠️ Important:</p>
            <p className="text-sm">Bookings must be made at least 2 hours in advance. An admin will contact you with payment details.</p>
          </div>
        </div>
      )
    },
    {
      id: 'cancellation',
      question: 'What is the cancellation and refund policy?',
      answer: (
        <div className="space-y-3">
          <div className="bg-red-50 border-2 border-red-300 p-4 rounded">
            <h4 className="font-bold text-red-800 mb-2"><span className="notranslate" translate="no">Karaoke</span> Booking Policy:</h4>
            <ul className="space-y-2 text-sm">
              <li className="font-bold text-red-700">STRICTLY NO REFUNDS for all confirmed bookings.</li>
              <li>Please ensure prompt payment after receiving the onePA payment link from Admin.</li>
              <li>A payment reminder will be sent <strong>7 days before</strong> your booking.</li>
              <li>No bookings or modifications within <strong>2 hours</strong> of session start time.</li>
            </ul>
          </div>
          <div className="bg-blue-50 border-2 border-blue-300 p-4 rounded">
            <h4 className="font-bold text-blue-800 mb-2">Date Changes (Exceptions):</h4>
            <p className="text-sm">In exceptional circumstances, the Admin may allow a one-time <strong>date change</strong> on a case-by-case basis. This is not guaranteed. Contact Admin at <strong>{ORG_PHONE}</strong> to discuss.</p>
          </div>
        </div>
      )
    }
  ];

  // FAQ Data for BBQ Pit
  const bbqFaqs = [
    {
      id: 'bbq-pricing',
      question: 'What are the BBQ Pit Booking prices?',
      answer: (
        <div className="space-y-3">
          <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded">
            <h4 className="font-bold text-orange-800 mb-2">🔥 <span className="notranslate" translate="no">BBQ Pit Booking</span> Pricing</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white p-3 border-2 border-black rounded">
                <p className="font-bold notranslate" translate="no">$20</p>
                <p className="text-sm text-gray-600">Per <span className="notranslate" translate="no">Pit</span>, Per Session</p>
              </div>
              <div className="bg-white p-3 border-2 border-black rounded">
                <p className="font-bold notranslate" translate="no">$40</p>
                <p className="text-sm text-gray-600"><span className="notranslate" translate="no">Both Pits (Pit 1 + Pit 2)</span></p>
              </div>
            </div>
          </div>
          <div className="text-sm space-y-1">
            <p><strong>Available <span className="notranslate" translate="no">Pits</span>:</strong> <span className="notranslate" translate="no">Pit 1</span> and <span className="notranslate" translate="no">Pit 2</span></p>
            <p><strong>Time Slots:</strong> 1:00 PM - 5:00 PM (Afternoon) or 6:00 PM - 10:00 PM (Evening)</p>
            <p><strong>Same price</strong> for weekdays, weekends, and public holidays</p>
          </div>
        </div>
      )
    },
    {
      id: 'bbq-booking',
      question: 'How do I book a BBQ Pit Booking?',
      answer: (
        <div className="space-y-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>Go to the <strong><span className="notranslate" translate="no">BBQ Pit Booking</span></strong> page from the home screen</li>
            <li>View the calendar to see available dates and <span className="notranslate" translate="no">pits</span></li>
            <li>Click on an available slot (green = available, red = booked)</li>
            <li>Select your <span className="notranslate" translate="no">pit</span> preference:
              <ul className="ml-6 mt-1 space-y-1 text-sm">
                <li>• <strong className="notranslate" translate="no">Pit 1</strong> - <span className="notranslate" translate="no">$20</span></li>
                <li>• <strong className="notranslate" translate="no">Pit 2</strong> - <span className="notranslate" translate="no">$20</span></li>
                <li>• <strong className="notranslate" translate="no">Both Pits</strong> - <span className="notranslate" translate="no">$40</span></li>
              </ul>
            </li>
            <li>Fill in your contact details (Name, Block/Unit, Mobile, Email)</li>
            <li>Acknowledge the no-refund policy</li>
            <li>Submit your booking request</li>
          </ol>
          <div className="bg-amber-50 border-2 border-amber-300 p-3 rounded">
            <p className="text-sm font-bold text-amber-800">Payment Instructions:</p>
            <p className="text-sm mt-1">After booking, an Admin will contact you with the onePA payment link. Please ensure prompt payment to confirm your booking.</p>
          </div>
        </div>
      )
    },
    {
      id: 'bbq-who',
      question: 'Who can book the BBQ Pit Booking?',
      answer: (
        <div className="space-y-3">
          <div className="bg-green-50 border-2 border-green-300 p-4 rounded">
            <h4 className="font-bold text-green-800 mb-2">✅ <span className="notranslate" translate="no">BBQ Pits</span> are Open to Everyone!</h4>
            <ul className="text-sm space-y-2">
              <li>• <strong>Residents and non-residents</strong> are welcome to book</li>
              <li>• You only need to provide <strong>valid contact details</strong>:</li>
              <ul className="ml-4 mt-1 space-y-1">
                <li>- Full Name</li>
                <li>- Block/Unit (for location reference)</li>
                <li>- Mobile Number</li>
                <li>- Email Address</li>
              </ul>
              <li>• Contact information is required for booking confirmations and communications</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            The <span className="notranslate" translate="no">BBQ Pit Booking</span> facilities are managed by <span className="notranslate" translate="no">YCK Court RN</span> and available to the wider community.
          </p>
        </div>
      )
    },
    {
      id: 'bbq-cancellation',
      question: 'Can I cancel my BBQ Pit Booking?',
      answer: (
        <div className="space-y-3">
          <div className="bg-red-50 border-2 border-red-300 p-4 rounded">
            <h4 className="font-bold text-red-800 mb-2"><span className="notranslate" translate="no">BBQ Pit Booking</span> Policy</h4>
            <ul className="text-sm space-y-2">
              <li className="font-bold text-red-700">STRICTLY NO REFUNDS once payment is confirmed.</li>
              <li>No bookings or modifications within <strong>2 hours</strong> of the event.</li>
              <li>In exceptional cases, contact Admin at <strong>{ORG_PHONE}</strong> to discuss a possible date change.</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600 italic">
            You must acknowledge the no-refund policy before completing your booking.
          </p>
        </div>
      )
    }
  ];

  // FAQ Data for Admins
  const adminFaqs = [
    {
      id: 'special-booking',
      question: 'How do I create a Special Booking?',
      answer: (
        <div className="space-y-4">
          <div className="bg-purple-50 border-2 border-purple-300 p-4 rounded">
            <h4 className="font-bold text-purple-800 mb-3">📋 Step-by-Step Guide:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Navigate to <strong><span className="notranslate" translate="no">Karaoke</span> tab</strong> in Admin Panel</li>
              <li>Click on <strong>&quot;Special Booking&quot;</strong> card (Guest/Partner)</li>
              <li>Fill in the booking form:
                <ul className="ml-6 mt-2 space-y-1">
                  <li>• <strong>Guest Name:</strong> Name of the booker</li>
                  <li>• <strong>Email & Phone:</strong> Contact details</li>
                  <li>• <strong>Date & Time Slot:</strong> Select booking date and session</li>
                  <li>• <strong>Discount Type:</strong> Choose rate category</li>
                </ul>
              </li>
              <li>Configure the <strong><span className="notranslate" translate="no">KJ</span> Required</strong> toggle (see below)</li>
              <li>Add any notes if needed</li>
              <li>Click <strong>&quot;CREATE SPECIAL BOOKING&quot;</strong></li>
            </ol>
          </div>
          
          <div className="bg-blue-50 border-2 border-blue-300 p-4 rounded">
            <h4 className="font-bold text-blue-800 mb-2">💰 Discount Types Available:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="bg-white p-2 border rounded">
                <p className="font-bold">Standard Rate</p>
                <p className="text-gray-600">Full price (<span className="notranslate" translate="no">$90</span>/<span className="notranslate" translate="no">$100</span>)</p>
              </div>
              <div className="bg-white p-2 border rounded">
                <p className="font-bold"><span className="notranslate" translate="no">RN</span> Member (50%)</p>
                <p className="text-gray-600">Half price for residents</p>
              </div>
              <div className="bg-white p-2 border rounded">
                <p className="font-bold">Community Partner (50%)</p>
                <p className="text-gray-600">Half price for partners</p>
              </div>
              <div className="bg-white p-2 border rounded">
                <p className="font-bold">Own Event (100%)</p>
                <p className="text-gray-600">Free - auto-confirmed</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'kj-toggle',
      question: 'What does the "KJ Required" toggle do?',
      answer: (
        <div className="space-y-4">
          <div className="bg-amber-50 border-2 border-amber-400 p-4 rounded">
            <h4 className="font-bold text-amber-800 mb-2">⚡ <span className="notranslate" translate="no">KJ</span> Required Toggle Explained:</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-white p-3 rounded border">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-bold text-green-700"><span className="notranslate" translate="no">KJ</span> Required = YES (Checked)</p>
                  <ul className="text-sm text-gray-600 mt-1 space-y-1">
                    <li>• An &quot;Assign <span className="notranslate" translate="no">KJ</span>&quot; dropdown appears</li>
                    <li>• Select a <span className="notranslate" translate="no">KJ</span> from the roster</li>
                    <li>• <span className="notranslate" translate="no">External KJ</span> gets <span className="notranslate" translate="no">$35</span>, <span className="notranslate" translate="no">RN Member</span> gets <span className="notranslate" translate="no">$0</span></li>
                    <li>• Allowance is recorded in the <span className="notranslate" translate="no">KJ</span> Allowance Report</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white p-3 rounded border">
                <AlertTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-bold text-orange-700"><span className="notranslate" translate="no">KJ</span> Required = NO (Unchecked)</p>
                  <ul className="text-sm text-gray-600 mt-1 space-y-1">
                    <li>• No <span className="notranslate" translate="no">KJ</span> is assigned to the booking</li>
                    <li>• <strong>No allowance is generated</strong> in the report</li>
                    <li>• Use for self-service or private events</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-red-50 border-2 border-red-300 p-3 rounded">
            <p className="text-sm font-bold text-red-700">⚠️ Important:</p>
            <p className="text-sm">If you accidentally leave &quot;<span className="notranslate" translate="no">KJ</span> Required&quot; unchecked, no allowance will be recorded even if a <span className="notranslate" translate="no">KJ</span> actually hosts the session!</p>
          </div>
        </div>
      )
    },
    {
      id: 'kj-roster',
      question: 'How do I manage the KJ Roster and change roles?',
      answer: (
        <div className="space-y-4">
          <div className="bg-indigo-50 border-2 border-indigo-300 p-4 rounded">
            <h4 className="font-bold text-indigo-800 mb-3">👥 Managing <span className="notranslate" translate="no">KJ</span> Roles:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Go to <strong>Directory</strong> tab in Admin Panel</li>
              <li>Click <strong>&quot;<span className="notranslate" translate="no">KJ</span> Roster&quot;</strong></li>
              <li>Find the member you want to update</li>
              <li>Click the <strong>allowance button</strong> (🎤 <span className="notranslate" translate="no">$35</span> or 🏠 <span className="notranslate" translate="no">$0</span>) to toggle their role</li>
              <li>Confirm the change in the popup</li>
              <li>The member will receive an email notification</li>
            </ol>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-green-100 p-3 rounded border-2 border-green-400">
              <p className="font-bold text-green-700">🎤 <span className="notranslate" translate="no">External KJ</span></p>
              <p className="text-sm"><span className="notranslate" translate="no">$35</span> per completed session</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded border-2 border-yellow-400">
              <p className="font-bold text-yellow-700">🏠 <span className="notranslate" translate="no">RN Member</span></p>
              <p className="text-sm"><span className="notranslate" translate="no">$0</span> allowance (volunteer)</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'admin-login',
      question: 'Where is the Admin Login?',
      answer: (
        <div className="space-y-3">
          <div className="bg-gray-100 border-2 border-gray-400 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-2">🔐 Admin Login Location:</h4>
            <p className="text-sm">The Admin Login is <strong>discreetly located</strong> on the <strong>BBQ Booking page</strong>.</p>
            <ol className="list-decimal list-inside mt-3 space-y-2 text-sm">
              <li>Go to the BBQ Booking page (<span className="notranslate" translate="no">/bbq</span>)</li>
              <li>Look for the <strong>Shield icon</strong> (🛡️) in the top-right header</li>
              <li>Click it to go to the Admin Login page</li>
              <li>Select the <strong>&quot;ADMIN&quot;</strong> tab</li>
              <li>Enter your admin credentials</li>
            </ol>
          </div>
          <p className="text-xs text-gray-500 italic">
            Note: The Admin button is hidden from the main landing page for security.
          </p>
        </div>
      )
    },
    {
      id: 'allowance-report',
      question: 'How does the KJ Allowance Report work?',
      answer: (
        <div className="space-y-3">
          <div className="bg-teal-50 border-2 border-teal-300 p-4 rounded">
            <h4 className="font-bold text-teal-800 mb-2">📊 Allowance Report Logic:</h4>
            <ul className="space-y-2 text-sm">
              <li>• Only <strong>completed</strong> bookings with &quot;<span className="notranslate" translate="no">KJ</span> Required = Yes&quot; appear</li>
              <li>• <span className="notranslate" translate="no">External KJ</span>: <span className="notranslate" translate="no">$35</span> per session</li>
              <li>• <span className="notranslate" translate="no">RN Member</span>: <span className="notranslate" translate="no">$0</span> per session (recorded but no payment)</li>
              <li>• Uses <strong>historical status</strong> - the <span className="notranslate" translate="no">KJ</span>&apos;s role at booking time</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            Access the report via: <strong>Finance tab → <span className="notranslate" translate="no">KJ</span> Allowance</strong>
          </p>
        </div>
      )
    }
  ];

  // Privacy & Data Protection FAQs
  const privacyFaqs = [
    {
      id: 'privacy-what',
      question: 'What personal data do you collect?',
      answer: (
        <div className="space-y-3">
          <div className="bg-blue-50 border-2 border-blue-300 p-4 rounded">
            <p className="font-bold text-blue-800 mb-2">We collect only what is necessary for bookings:</p>
            <ul className="text-sm space-y-1 ml-4 list-disc">
              <li><strong>Name</strong> — to identify the booking holder</li>
              <li><strong>Email</strong> — for confirmations, receipts, and reminders</li>
              <li><strong>Mobile number</strong> — for Admin to coordinate payment</li>
              <li><strong>Block/Unit</strong> — for location reference</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            We do <strong>not</strong> collect NRIC, bank details, or any sensitive personal data.
          </p>
        </div>
      )
    },
    {
      id: 'privacy-visible',
      question: 'Can other people see my booking details?',
      answer: (
        <div className="space-y-3">
          <div className="bg-green-50 border-2 border-green-300 p-4 rounded">
            <p className="font-bold text-green-800 mb-2">Your privacy is protected:</p>
            <ul className="text-sm space-y-2 ml-4 list-disc">
              <li>On the public calendar, only a <strong>masked version</strong> of your name is shown (e.g., &quot;Jo** T**&quot;).</li>
              <li>Your <strong>email, phone number, and address are never displayed</strong> publicly.</li>
              <li>Only authorized Admins can view your full contact details for operational purposes.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'privacy-retention',
      question: 'How long do you keep my data?',
      answer: (
        <div className="space-y-3">
          <div className="bg-amber-50 border-2 border-amber-300 p-4 rounded">
            <p className="font-bold text-amber-800 mb-2">Data Retention Periods:</p>
            <ul className="text-sm space-y-2 ml-4 list-disc">
              <li><strong>Active bookings:</strong> Retained while the booking is active.</li>
              <li><strong>Completed bookings:</strong> 3 years (for financial audit and tax compliance).</li>
              <li><strong>Cancelled bookings:</strong> 1 year, after which your personal data is <strong>automatically anonymized</strong>.</li>
              <li><strong>Member accounts:</strong> Until you request deletion, or after 2 years of inactivity.</li>
              <li><strong>Password reset codes:</strong> Automatically deleted after 24 hours.</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            Our system runs an automated PDPA cleanup daily to enforce these retention periods.
          </p>
        </div>
      )
    },
    {
      id: 'privacy-rights',
      question: 'What are my data protection rights?',
      answer: (
        <div className="space-y-3">
          <div className="bg-purple-50 border-2 border-purple-300 p-4 rounded">
            <p className="font-bold text-purple-800 mb-2">Under the PDPA, you have the right to:</p>
            <ul className="text-sm space-y-2 ml-4 list-disc">
              <li><strong>Access</strong> — Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction</strong> — Request correction of inaccurate or incomplete data.</li>
              <li><strong>Withdrawal</strong> — Withdraw your consent for data processing at any time.</li>
              <li><strong>Deletion</strong> — Request deletion of your personal data.</li>
            </ul>
          </div>
          <div className="bg-blue-50 border-2 border-blue-300 p-3 rounded mt-2">
            <p className="font-bold text-blue-800 text-sm">How to exercise your rights:</p>
            <p className="text-sm mt-1">
              Email our Data Protection Officer at <strong>{ORG_EMAIL_BOOKINGS}</strong> with the subject 
              line &quot;PDPA Request&quot;. We will respond within 30 business days.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'privacy-security',
      question: 'How do you protect my data?',
      answer: (
        <div className="space-y-3">
          <div className="bg-slate-50 border-2 border-slate-300 p-4 rounded">
            <p className="font-bold text-slate-800 mb-2">Security Measures:</p>
            <ul className="text-sm space-y-2 ml-4 list-disc">
              <li><strong>Encryption</strong> — Passwords are hashed using industry-standard bcrypt.</li>
              <li><strong>Session security</strong> — Automatic logout after 30 minutes of inactivity.</li>
              <li><strong>Role-based access</strong> — Strict separation between Admin, KJ, and public access.</li>
              <li><strong>Name masking</strong> — Full names are never shown on public-facing pages.</li>
              <li><strong>Automated cleanup</strong> — Expired tokens and old data are automatically purged.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'privacy-breach',
      question: 'What happens if there is a data breach?',
      answer: (
        <div className="space-y-3">
          <div className="bg-red-50 border-2 border-red-300 p-4 rounded">
            <p className="font-bold text-red-800 mb-2">Data Breach Response:</p>
            <ul className="text-sm space-y-2 ml-4 list-disc">
              <li>We will notify the <strong>Personal Data Protection Commission (PDPC)</strong> within 3 calendar days.</li>
              <li>Affected individuals will be notified as soon as practicable.</li>
              <li>Immediate steps will be taken to contain and remediate the breach.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'privacy-dpo',
      question: 'Who is the Data Protection Officer?',
      answer: (
        <div className="space-y-3">
          <div className="bg-gray-50 border-2 border-gray-300 p-4 rounded">
            <p className="font-bold text-gray-800 mb-2">Data Protection Officer (DPO):</p>
            <p className="text-sm">{ORG_FULL_NAME}</p>
            <p className="text-sm mt-1">Email: <strong>{ORG_EMAIL_BOOKINGS}</strong></p>
            <p className="text-sm">Phone: <strong>{ORG_PHONE}</strong></p>
          </div>
          <p className="text-sm text-gray-600">
            For the full privacy policy, click the &quot;PDPA &amp; Privacy Policy&quot; checkbox link on the booking form, 
            or contact the DPO directly.
          </p>
        </div>
      )
    }
  ];

  const currentFaqs = activeTab === 'karaoke' ? residentFaqs : activeTab === 'bbq' ? bbqFaqs : activeTab === 'privacy' ? privacyFaqs : adminFaqs;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-white/20 rounded"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-syne font-bold flex items-center gap-2">
                  <HelpCircle size={24} /> Help & FAQ
                </h1>
                <p className="text-xs text-white/80">User Guide & Admin Workflow</p>
              </div>
            </div>
            <LanguageSelector variant="dark" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Quick Reference Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg border-4 border-black mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Star className="text-yellow-300" /> Quick Reference
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white/20 p-3 rounded text-center">
              <p className="text-xl font-bold notranslate" translate="no">$90</p>
              <p className="text-xs"><span className="notranslate" translate="no">Karaoke</span> Weekday</p>
            </div>
            <div className="bg-white/20 p-3 rounded text-center">
              <p className="text-xl font-bold notranslate" translate="no">$100</p>
              <p className="text-xs"><span className="notranslate" translate="no">Karaoke</span> Wknd/PH</p>
            </div>
            <div className="bg-white/20 p-3 rounded text-center">
              <p className="text-xl font-bold notranslate" translate="no">$20</p>
              <p className="text-xs"><span className="notranslate" translate="no">BBQ Pit Booking</span> (each)</p>
            </div>
            <div className="bg-white/20 p-3 rounded text-center">
              <p className="text-xl font-bold notranslate" translate="no">$40</p>
              <p className="text-xs"><span className="notranslate" translate="no">BBQ Both Pits</span></p>
            </div>
            <div className="bg-white/20 p-3 rounded text-center">
              <p className="text-xl font-bold notranslate" translate="no">$35</p>
              <p className="text-xs notranslate" translate="no">External KJ</p>
            </div>
            <div className="bg-white/20 p-3 rounded text-center">
              <p className="text-xl font-bold notranslate" translate="no">$0</p>
              <p className="text-xs notranslate" translate="no">RN Member</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => { setActiveTab('karaoke'); setOpenSection(null); }}
            className={`flex-1 min-w-[120px] py-3 px-4 font-bold text-sm border-2 border-black transition-all ${
              activeTab === 'karaoke' 
                ? 'bg-primary text-white' 
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Music className="inline mr-2" size={18} />
            <span className="notranslate" translate="no">Karaoke</span>
          </button>
          <button
            onClick={() => { setActiveTab('bbq'); setOpenSection(null); }}
            className={`flex-1 min-w-[120px] py-3 px-4 font-bold text-sm border-2 border-black transition-all ${
              activeTab === 'bbq' 
                ? 'bg-orange-500 text-white' 
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Flame className="inline mr-2" size={18} />
            <span className="notranslate" translate="no">BBQ Pit Booking</span>
          </button>
          {/* Admin tab - only visible when logged in as admin */}
          {isAdmin && (
            <button
              onClick={() => { setActiveTab('admin'); setOpenSection(null); }}
              className={`flex-1 min-w-[120px] py-3 px-4 font-bold text-sm border-2 border-black transition-all ${
                activeTab === 'admin' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white hover:bg-gray-100'
              }`}
            >
              <Shield className="inline mr-2" size={18} />
              Admin
            </button>
          )}
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3">
          {currentFaqs.map((faq) => (
            <div 
              key={faq.id} 
              className="bg-white border-2 border-black rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleSection(faq.id)}
                className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-bold text-sm sm:text-base pr-4">{faq.question}</span>
                {openSection === faq.id ? (
                  <ChevronUp className="flex-shrink-0 text-gray-500" size={20} />
                ) : (
                  <ChevronDown className="flex-shrink-0 text-gray-500" size={20} />
                )}
              </button>
              {openSection === faq.id && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  <div className="pt-4">
                    {faq.answer}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* SOP Download - Conditional based on active tab */}
        {activeTab === 'karaoke' && (
          <div className="mt-8 space-y-4">
            <div className="bg-purple-50 border-2 border-purple-400 p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                <FileDown className="text-purple-600" /> <span className="notranslate" translate="no">KJ</span> Guidelines &amp; Room SOP
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Download the official <span className="notranslate" translate="no">KJ</span> Guidelines and Karaoke Room Standard Operating Procedure. Includes session timing, responsibilities, equipment rules, and emergency contacts.
              </p>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/kj-guidelines-pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-purple-600 text-white font-bold border-2 border-black hover:bg-purple-700 transition-colors"
                data-testid="download-kj-guidelines-btn"
              >
                <FileDown size={18} />
                DOWNLOAD PDF
              </a>
            </div>

            <div className="bg-indigo-50 border-2 border-indigo-400 p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                <FileDown className="text-indigo-600" /> Room Booking Guidelines (Without <span className="notranslate" translate="no">KJ</span>)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                For room bookings without a <span className="notranslate" translate="no">KJ</span>. Includes room access, equipment restrictions, aircon usage, cleanup rules, and emergency contacts.
              </p>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/room-booking-sop-pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white font-bold border-2 border-black hover:bg-indigo-700 transition-colors"
                data-testid="download-room-booking-sop-btn"
              >
                <FileDown size={18} />
                DOWNLOAD PDF
              </a>
            </div>
          </div>
        )}

        {activeTab === 'bbq' && (
          <div className="mt-8 bg-orange-50 border-2 border-orange-400 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <FileDown className="text-orange-600" /> <span className="notranslate" translate="no">BBQ Pit</span> Guidelines &amp; SOP
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Download the official <span className="notranslate" translate="no">BBQ Pit</span> Standard Operating Procedure. Includes pit usage rules, cleanup instructions, toilet timings, safety guidelines, and emergency contacts.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/bbq-pit-sop-pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-orange-500 text-white font-bold border-2 border-black hover:bg-orange-600 transition-colors"
              data-testid="download-bbq-sop-btn"
            >
              <FileDown size={18} />
              DOWNLOAD PDF
            </a>
          </div>
        )}

        {/* Contact Section */}
        <div className="mt-8 bg-white border-2 border-black p-6 rounded-lg">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Phone className="text-primary" /> Need More Help?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a 
              href={`tel:${ORG_PHONE_RAW}`} 
              className="flex items-center gap-3 p-3 bg-green-50 border-2 border-green-300 rounded hover:bg-green-100 transition-colors"
            >
              <Phone className="text-green-600" size={24} />
              <div>
                <p className="font-bold">Call Us</p>
                <p className="text-sm text-gray-600 notranslate" translate="no">{ORG_PHONE}</p>
              </div>
            </a>
            <a 
              href={`mailto:${ORG_EMAIL_BOOKINGS}`} 
              className="flex items-center gap-3 p-3 bg-blue-50 border-2 border-blue-300 rounded hover:bg-blue-100 transition-colors"
            >
              <Mail className="text-blue-600" size={24} />
              <div>
                <p className="font-bold">Email Us</p>
                <p className="text-sm text-gray-600 notranslate" translate="no">{ORG_EMAIL_BOOKINGS}</p>
              </div>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpPage;
